/**
 * Haunted house scenes — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const hauntedHouseScenesPack: ToilePack = {
    slug: 'haunted-house-scenes',
    category: 'season',
    name: 'Haunted house scenes',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'hill', label: 'On the hill', subject: 'A haunted mansion on a hill with a crooked path, bare trees and a full moon, bats, no text'},
        {key: 'section', label: 'Cross-section', subject: 'A cross-section of a haunted house showing rooms with a ghost, a cauldron, a coffin and an attic bat, no text'},
        {key: 'porch', label: 'Front porch', subject: 'A haunted house porch at night with carved pumpkins on the steps and a lit door, fog, no text'},
    ],
    styles: [
        {key: 'cute', label: 'Spooky cute', style: 'kawaii spooky illustration, rounded shapes, soft outlines, friendly faces', palette: 'Pastel purple, orange, mint and cream with black accents', framing: 'Centered on a solid background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage', style: '1950s halloween paper decoration style, halftone grain, slight misregistration', palette: 'Orange, black, cream and a faded green', framing: 'Centered on an aged cream paper background with an even margin'},
        {key: 'bold', label: 'Bold graphic', style: 'bold graphic illustration, thick outlines, high contrast, flat fills', palette: 'Orange, black and white with a single purple', framing: 'Centered on a solid background with a little room around it'},
    ],
    directions: ['in daylight instead of night', 'with a full moon eclipse', 'as a wider panorama'],
    directionHint: 'try: in daylight instead of night',
    seo: {
        title: 'Haunted House Scenes: Hill, Cutaway, Porch Art',
        metaDescription: 'Nine haunted house illustrations -- on the hill, cross-section, front porch -- in spooky cute, vintage, and bold graphic finishes.',
        h1: 'Haunted house illustration: on the hill, cross-section, and front porch',
        tagline: 'Nine scenes, three house views, three finishes each.',
        intro: 'Nine illustrated haunted house scenes: a crooked mansion on a hill under a full moon, a cross-section revealing rooms with a ghost and a cauldron, and a front porch lit for trick-or-treaters. Three finishes -- spooky cute, vintage, and bold graphic -- for prints, cards, and halloween decor. Ask for a daylight version instead, or a wider panorama, and the same three finishes carry it.',
        useCases: [
            'a halloween card illustration',
            'a framed wall print',
            'a puzzle or poster image',
            'a halloween shop backdrop',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine storybook illustrations -- a haunted house on a hill, a cutaway cross-section, and a lit front porch -- each in spooky cute, vintage, and bold graphic finishes, flat and text-free.'},
            {q: 'What size do these print at?', a: 'The scenes render at a wide landscape ratio, suitable for a standard card, a framed print, or a poster.'},
            {q: 'Is there any text on the illustrations?', a: 'No -- every scene is text-free, ready for your own caption or greeting to be added separately.'},
            {q: 'Can I get a different view, like daylight?', a: 'Yes -- the direction field takes any variation, including in daylight instead, a full moon eclipse, or a wider panorama.'},
        ],
    },
}
